import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Outlet, useLocation } from "react-router-dom";

import { DocumentationSidebar } from "./DocumentationSidebar.js";

/**
 * Documentation section shell.
 *
 * On every reader surface (`/documentation`, `/documentation/type/*`,
 * `/documentation/library/*`) the layout renders a sticky folder-tree
 * sidebar on the left and the page content on the right. The sidebar
 * is suppressed on the editor surfaces (`/documentation/manage/*`,
 * `/documentation/categories`) so the create/upload/AI flows and the
 * categories admin list keep their full-width chrome — those pages
 * already own a dense form layout that the tree would crowd.
 *
 * On narrow viewports we drop to a single column with the tree
 * collapsed above the content. Operators primarily browse the docs on
 * a desktop, and the tree's value (always-on context) doesn't survive
 * a vertical stack — so on mobile we collapse it behind a `<details>`
 * disclosure instead. That keeps the route shell stable while still
 * giving phone users a way to navigate.
 */
export function DocumentationLayout(): JSX.Element {
  const location = useLocation();
  const path = location.pathname;
  // Editor surfaces own their full-width layout; the tree only mounts
  // on the reader surfaces where always-on navigation pays off.
  const hideSidebar =
    path.includes("/documentation/manage") ||
    path.includes("/documentation/categories") ||
    // The access surface has its own left rail (the account picker), and two
    // stacked rails would be a maze rather than navigation.
    path.includes("/documentation/access") ||
    // Online Training authors a module's own topic menu beside its video —
    // the folder tree next to it would be a second, unrelated menu.
    path.includes("/documentation/online-training");

  if (hideSidebar) return <Outlet />;

  return (
    <Box sx={{ display: "grid", gap: 3, gridTemplateColumns: { lg: "260px 1fr" } }}>
      <Box component="aside" sx={{ display: { xs: "none", lg: "block" } }}>
        <Box
          sx={{
            position: "sticky",
            top: 16,
            maxHeight: "calc(100vh - 6rem)",
            overflowY: "auto",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
            p: 1,
          }}
        >
          <DocumentationSidebar />
        </Box>
      </Box>

      <Box
        component="details"
        sx={{
          display: { lg: "none" },
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
          p: 1,
          fontSize: 14,
        }}
      >
        <Box component="summary" sx={{ cursor: "pointer", userSelect: "none", px: 0.5, py: 0.5, fontWeight: 500, color: "text.primary" }}>
          Browse the documentation
        </Box>
        <Box sx={{ mt: 1 }}>
          <DocumentationSidebar />
        </Box>
      </Box>

      <Box component="main" sx={{ minWidth: 0 }}>
        <Outlet />
      </Box>
    </Box>
  );
}
